import React from "react";
import { Pencil, Plus, Trash2, UserPlus } from "lucide-react";
import Avatar from "./Avatar";
import { useTheme } from "../ThemeProvider";
import { getSeliseCSuiteColor, getThemeColor, hexToRgba } from "./seliseColors";
import ReorderContainer from "./ReorderContainer";
import CategoryBadgeColorPicker from "./CategoryBadgeColorPicker";

function BoardCard({ node, isAdmin, onEdit, onDelete, onView, large }) {
  return (
    <div className="relative group transition-all duration-300 hover:-translate-y-1">
      {isAdmin && (
        <div className="absolute -top-2 -right-2 flex gap-1 z-10">
          <button
            onClick={() => onEdit(node)}
            title="Edit"
            className="p-1 bg-white rounded-full shadow border border-gray-200 text-gray-600 hover:text-blue-600"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDelete(node.id)}
            title="Delete"
            className="p-1 bg-white rounded-full shadow border border-gray-200 text-gray-600 hover:text-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
      <div
        onClick={() => !isAdmin && onView?.(node)}
        style={{ width: "170px", height: "130px", cursor: !isAdmin ? "pointer" : "grab", boxShadow: node.is_vacancy ? "none" : "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px -2px rgba(0,0,0,0.08)" }}
        className={`rounded-xl px-3 py-2 text-center flex flex-col items-center justify-center transition-shadow duration-300 group-hover:shadow-lg ${node.is_vacancy ? "border-2 border-dashed border-gray-300 bg-gray-50/50" : "border border-gray-200/80 bg-white group-hover:border-gray-300"}`}
      >
        {node.is_vacancy ? (
          <UserPlus className="w-6 h-6 text-gray-400 mb-1" />
        ) : (
          <Avatar name={node.name} photoUrl={node.photo_url} size={large ? "md" : "sm"} />
        )}
        <p className={`font-bold text-gray-900 mt-1 ${large ? "text-sm" : "text-xs"}`}>
          {node.name}
        </p>
        {node.is_vacancy ? (
          <p className="text-[10px] font-medium text-blue-600">Open Position</p>
        ) : node.title ? (
          <p className="text-[10px] text-gray-500">{node.title}</p>
        ) : null}
      </div>
    </div>
  );
}

function CSuiteCard({ node, index, isAdmin, onEdit, onDelete, onView }) {
  const { theme } = useTheme();
  const headerColor = getThemeColor(
    node.color,
    theme,
    theme === "selise" || theme === "dark" ? getSeliseCSuiteColor(index) : "#3465a4"
  );
  return (
    <div className="relative group transition-all duration-300 hover:-translate-y-1">
      {isAdmin && (
        <div className="absolute -top-2 -right-2 flex gap-1 z-10">
          <button
            onClick={() => onEdit(node)}
            title="Edit"
            className="p-1 bg-white rounded-full shadow border border-gray-200 text-gray-600 hover:text-blue-600"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDelete(node.id)}
            title="Delete"
            className="p-1 bg-white rounded-full shadow border border-gray-200 text-gray-600 hover:text-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
      <div
        onClick={() => !isAdmin && onView?.(node)}
        style={{ width: "170px", height: "130px", cursor: !isAdmin ? "pointer" : "grab", boxShadow: node.is_vacancy ? "none" : "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px -2px rgba(0,0,0,0.08)" }}
        className={`rounded-xl overflow-hidden flex flex-col transition-shadow duration-300 group-hover:shadow-lg ${node.is_vacancy ? "border-2 border-dashed border-gray-300" : "border border-gray-200/80 group-hover:border-gray-300"}`}
      >
        <div className={`px-3 py-2 text-center flex-1 flex flex-col items-center justify-center ${node.is_vacancy ? "bg-gray-50/50" : "bg-white"}`}>
          {node.is_vacancy ? (
            <UserPlus className="w-5 h-5 text-gray-400 mb-1" />
          ) : (
            <Avatar name={node.name} photoUrl={node.photo_url} size="sm" />
          )}
          <p className="font-bold text-gray-900 mt-1 text-xs">{node.name}</p>
          {node.is_vacancy ? (
            <p className="text-[10px] font-medium text-blue-600">Open Position</p>
          ) : node.full_title ? (
            <p className="text-[10px] text-gray-500">{node.full_title}</p>
          ) : null}
        </div>
        <div className="px-3 py-1" style={{ backgroundColor: headerColor }}>
          <p className="text-white text-sm font-bold text-center">{node.title}</p>
        </div>
      </div>
    </div>
  );
}

export default function BoardSection({
  boardNodes,
  delegateNode,
  cSuiteNodes,
  isAdmin,
  onEditNode,
  onAddNode,
  onDeleteNode,
  onView,
  onReorderBoard,
  onReorderCSuite,
  boardColor = "#2563eb",
  onBoardColorChange,
  theme,
  dividerColor = "#caa326",
}) {
  const chairman = boardNodes.find((n) => n.order === 0);
  const advisory = boardNodes.filter((n) => n.order !== 0);
  const advisoryRow = [...advisory, ...(delegateNode ? [delegateNode] : [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  const badgeStyle = {
    backgroundColor: hexToRgba(boardColor, 0.12),
    color: boardColor,
    borderColor: hexToRgba(boardColor, 0.3),
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };

  return (
    <div className="flex flex-col items-center">
      {/* Chairman */}
      <div className="flex items-center gap-2 mb-4">
        <span
          className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-wider border uppercase"
          style={badgeStyle}
        >
          Board of Directors
        </span>
        {isAdmin && (
          <CategoryBadgeColorPicker
            color={boardColor}
            onChange={onBoardColorChange}
            theme={theme}
          />
        )}
      </div>
      {chairman ? (
        <BoardCard
          node={chairman}
          isAdmin={isAdmin}
          onEdit={onEditNode}
          onDelete={onDeleteNode}
          onView={onView}
          large
        />
      ) : isAdmin ? (
        <button
          onClick={() => onAddNode("board")}
          className="px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-400"
        >
          + Add Chairman
        </button>
      ) : null}

      <div className="h-6" />

      {/* Advisory row (includes delegate) */}
      {advisoryRow.length > 0 && (
        <div className="flex gap-4 flex-wrap justify-center items-start">
          <ReorderContainer
            items={advisoryRow}
            onReorder={onReorderBoard}
            droppableId="board-advisory"
            direction="horizontal"
            disabled={!isAdmin}
            className="flex gap-4 flex-wrap justify-center items-start"
            renderItem={(node, index, dragProvided) => (
              <div
                ref={dragProvided?.innerRef}
                {...dragProvided?.draggableProps}
                {...dragProvided?.dragHandleProps}
                className={isAdmin ? "cursor-grab active:cursor-grabbing" : ""}
              >
                <BoardCard
                  node={node}
                  isAdmin={isAdmin}
                  onEdit={onEditNode}
                  onDelete={onDeleteNode}
                  onView={onView}
                />
              </div>
            )}
          />
          {isAdmin && (
            <button
              onClick={() => onAddNode("board")}
              className="px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-400 self-center"
            >
              + Add
            </button>
          )}
        </div>
      )}

      <div className="h-4" />

      {/* C-Suite row */}
      {cSuiteNodes.length > 0 && (
        <>
        <span
          className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-wider border uppercase mb-4"
          style={badgeStyle}
        >
          Board of Executives
        </span>
        <div className="flex gap-6 items-start">
          <ReorderContainer
            items={cSuiteNodes}
            onReorder={onReorderCSuite}
            droppableId="c-suite"
            direction="horizontal"
            disabled={!isAdmin}
            className="flex gap-6 items-start"
            renderItem={(node, index, dragProvided) => (
              <div
                ref={dragProvided?.innerRef}
                {...dragProvided?.draggableProps}
                {...dragProvided?.dragHandleProps}
                className={isAdmin ? "cursor-grab active:cursor-grabbing" : ""}
              >
                <CSuiteCard
                  node={node}
                  index={index}
                  isAdmin={isAdmin}
                  onEdit={onEditNode}
                  onDelete={onDeleteNode}
                  onView={onView}
                />
              </div>
            )}
          />
          {isAdmin && (
            <button
              onClick={() => onAddNode("c_suite")}
              className="px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-400 self-center"
            >
              + Add
            </button>
          )}
        </div>
        {/* Vertical connector lines from each C-Suite card down to the gold divider */}
        {cSuiteNodes.length > 0 && (
          <div className="flex gap-6 justify-center -mb-4">
            {cSuiteNodes.map((node) => (
              <div key={node.id} style={{ width: "170px" }} className="flex justify-center">
                <div className="w-px h-8" style={{ backgroundColor: hexToRgba(dividerColor, 0.6) }} />
              </div>
            ))}
          </div>
        )}
        </>
      )}
    </div>
  );
}